import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaQrcode, FaCamera } from 'react-icons/fa';
import { userService } from '../services/userService';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { t } from '../i18n';

const UserSwitchModal = ({ show, onClose }) => {
  const { switchUser } = useAuth();
  const [qrInput, setQrInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const qrInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timeoutRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setQrInput('');
    setIsLoading(false);
    onClose();
  }, [stopCamera, onClose]);

  const handleQRInputSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!qrInput.trim() || isLoading) return;

    setIsLoading(true);

    try {
      const result = await userService.qrLogin(qrInput.trim());

      if (result.success && result.data) {
        await switchUser(result.data);
        toast.success(t('Switched to user: {username}', { username: result.data.username }));
        handleClose();
      } else {
        toast.error(result.error || t('Failed to switch user. Please check the QR code.'));
        setQrInput('');
        if (qrInputRef.current) {
          qrInputRef.current.focus();
        }
      }
    } catch (err) {
      toast.error(t('Error switching user. Please try again.'));
      setQrInput('');
      if (qrInputRef.current) {
        qrInputRef.current.focus();
      }
    } finally {
      setIsLoading(false);
    }
  }, [qrInput, isLoading, switchUser, handleClose]);

  useEffect(() => {
    if (show && qrInputRef.current) {
      qrInputRef.current.focus();
    }
    return () => {
      stopCamera();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [show, stopCamera]);

  useEffect(() => {
    if (qrInput.trim()) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        handleQRInputSubmit(new Event('submit'));
      }, 500);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [qrInput, handleQRInputSubmit]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsScanning(true);
      toast.info(t('Camera started. Point at QR code to scan.'));
    } catch (err) {
      toast.error(t('Failed to access camera. Please use barcode scanner instead.'));
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('Switch User')}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleQRInputSubmit} className="space-y-4">
          <div className="block md:hidden">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCamera className="inline h-4 w-4 mr-1" />
              {t('Camera Scanner')}
            </label>
            <div className="space-y-3">
              <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                {isScanning ? (
                  <div className="relative w-full h-full">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-4 border-2 border-primary border-dashed rounded-lg pointer-events-none">
                      <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-primary"></div>
                      <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-primary"></div>
                      <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-primary"></div>
                      <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-primary"></div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FaQrcode className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs text-gray-600">{t('Camera preview')}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                {!isScanning ? (
                  <button
                    type="button"
                    onClick={startCamera}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    <FaCamera className="h-4 w-4 mr-2" />
                    {t('Start Camera')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={stopCamera}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    {t('Stop Camera')}
                  </button>
                )}
              </div>
            </div>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-gray-500">{t('Or scan with barcode scanner')}</span>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="qr-input" className="block text-sm font-medium text-gray-700 mb-2">
              {t('Scan QR Code')}
            </label>
            <textarea
              id="qr-input"
              ref={qrInputRef}
              value={qrInput}
              onChange={(e) => setQrInput(e.target.value)}
              placeholder={t('Scan QR code here...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary h-24 resize-none"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={!qrInput.trim() || isLoading}
              className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('Switching...')}
                </>
              ) : (
                <>
                  <FaQrcode className="mr-2 h-4 w-4" />
                  {t('Switch User')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {t('Cancel')}
            </button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            <p>{t('Scan the QR code of the user you want to switch to.')}</p>
            <p className="mt-1">{t('Only users with QR login enabled can switch accounts.')}</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserSwitchModal;
